import { PlusIcon, TrashIcon } from "@heroicons/react/24/outline"
import type { ReactNode, ComponentType } from "react"

type SectionProps = {
  id?: string
  title: string
  children: ReactNode
  onAdd?: () => void
  addLabel?: string
  addIcon?: ComponentType<{ className?: string }>
  icon?: ComponentType<{ className?: string }>
  /** Tailwind classes for the icon chip, e.g. "text-blue-500 bg-blue-50" */
  iconColor?: string
  description?: string
}

export const Section = ({
  id, title, children, onAdd, addLabel, addIcon: AddIcon = PlusIcon, icon: Icon, iconColor, description,
}: SectionProps) => (
  <div
    id={id}
    className="scroll-mt-28 bg-white border border-gray-100 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-200"
  >
    {/* Header */}
    <div className="flex justify-between items-center px-5 py-4 border-b border-gray-50 bg-gray-50/60">
      <div className="flex items-center gap-2.5">
        {Icon && (
          <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${iconColor ?? "bg-white border border-gray-100 text-gray-500"}`}>
            <Icon className="w-4 h-4" />
          </div>
        )}
        <div>
          <h3 className="font-semibold text-gray-900 text-sm">{title}</h3>
          {description && <p className="text-xs text-gray-400 mt-0.5">{description}</p>}
        </div>
      </div>

      {onAdd && (
        <button
          onClick={onAdd}
          className="flex items-center gap-1.5 text-xs font-medium text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 px-3 py-1.5 rounded-lg transition flex-shrink-0"
        >
          <AddIcon className="w-3.5 h-3.5" />
          {addLabel ?? "Add"}
        </button>
      )}
    </div>

    {/* Content */}
    <div className="p-5 space-y-3">{children}</div>
  </div>
)

export const EmptyState = ({
  icon: Icon,
  title,
  subtitle,
  ctaLabel,
  onAdd,
}: {
  icon?: ComponentType<{ className?: string }>
  title: string
  subtitle?: string
  ctaLabel?: string
  onAdd?: () => void
}) => (
  <div className="flex flex-col items-center gap-1 text-center py-8 px-4 rounded-xl bg-gray-50/60">
    {Icon && (
      <div className="w-9 h-9 rounded-full bg-white border border-gray-100 flex items-center justify-center mb-1.5">
        <Icon className="w-4 h-4 text-gray-300" />
      </div>
    )}
    <p className="text-sm font-medium text-gray-600">{title}</p>
    {subtitle && <p className="text-xs text-gray-400 max-w-xs">{subtitle}</p>}
    {onAdd && (
      <button
        onClick={onAdd}
        className="mt-2.5 inline-flex items-center gap-1.5 text-xs font-medium text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 px-3 py-1.5 rounded-lg transition"
      >
        <PlusIcon className="w-3.5 h-3.5" />
        {ctaLabel ?? "Add"}
      </button>
    )}
  </div>
)

export const Item = ({
  title,
  subtitle,
  description,
  link,
  onDelete,
}: {
  title: string
  subtitle?: string
  description?: string
  link?: string
  onDelete: () => void
}) => (
  <div className="group flex items-start gap-3 p-3 rounded-xl hover:bg-gray-50 transition">
    {/* Timeline dot */}
    <div className="mt-1 w-2 h-2 rounded-full bg-blue-400 flex-shrink-0" />

    <div className="flex-1 min-w-0">
      <div className="font-medium text-sm text-gray-900">{title}</div>
      {subtitle && <div className="text-xs text-gray-500 mt-0.5">{subtitle}</div>}
      {description && <p className="text-sm text-gray-600 mt-1 leading-relaxed">{description}</p>}
      {link && (
        <a
          href={link}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-blue-600 hover:underline mt-1 inline-block"
        >
          View →
        </a>
      )}
    </div>

    <button
      onClick={onDelete}
      className="opacity-0 group-hover:opacity-100 p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition"
    >
      <TrashIcon className="w-4 h-4" />
    </button>
  </div>
)
